import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import bcrypt from 'bcryptjs';
import './Register.css';
import { getStudentByInviteSlug, updateStudent } from '../utils/studentsService';

const EMAILS_KEY = 'novas_registered_emails';

const Register = () => {
  const navigate = useNavigate();
  const { studentSlug } = useParams();
  const [formData, setFormData] = useState({
    role: 'teacher',
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreement: false,
    inviteCode: '' // Код приглашения для преподавателей
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showEmailCheck, setShowEmailCheck] = useState(false);
  const [resendStatus, setResendStatus] = useState(null); // null | 'sending' | 'sent' | 'error'
  // eslint-disable-next-line no-unused-vars
  const [sentCode, setSentCode] = useState(null);
  const [inviteData, setInviteData] = useState(null);
  const [isInviteMode, setIsInviteMode] = useState(false);
  const [inviteError, setInviteError] = useState(null);

  // Get registered emails
  const getRegisteredEmails = () => {
    try {
      return JSON.parse(localStorage.getItem(EMAILS_KEY)) || [];
    } catch {
      return [];
    }
  };

  // Save email
  // eslint-disable-next-line no-unused-vars
  const saveEmail = (email) => {
    const emails = getRegisteredEmails();
    emails.push(email);
    localStorage.setItem(EMAILS_KEY, JSON.stringify(emails));
  };

  // Check for invite link
  useEffect(() => {
    console.log('useEffect triggered with studentSlug:', studentSlug);
    console.log('Current URL:', window.location.href);
    
    if (studentSlug) {
      setIsLoading(true);
      // Загружаем студента из Supabase
      getStudentByInviteSlug(studentSlug)
        .then(student => {
          console.log('Student data retrieved from Supabase:', student);
        
        if (student) {
          setInviteData(student);
          setIsInviteMode(true);
          setInviteError(null);
            // НЕ устанавливаем имя из student.name - студент должен ввести свое имя
            // НО email берём из профиля — преподаватель его задал, студент
            // обязан использовать именно его, чтобы login работал.
            setFormData(prev => ({
              ...prev,
              role: 'student',
              email: student.email || prev.email
            }));
            console.log('Form data updated for invite mode, inviteData:', student);
            console.log('isInviteMode set to true');
          } else {
            console.error('Student not found for slug:', studentSlug);
            // Не устанавливаем ошибку, чтобы форма все равно показывалась
            // setInviteError(`Student not found for invite link. Please check the link or contact your teacher.`);
            // Но все равно устанавливаем режим студента, если есть slug
            setIsInviteMode(true);
            setFormData(prev => ({
              ...prev,
              role: 'student'
            }));
          }
        })
        .catch(error => {
          console.error('Error loading student:', error);
          // Не блокируем форму, просто показываем предупреждение
          console.warn('Could not load student data, but showing form anyway');
          // Устанавливаем режим студента, если есть slug
          setIsInviteMode(true);
          setFormData(prev => ({
            ...prev,
            role: 'student'
          }));
        })
        .finally(() => {
          setIsLoading(false);
        });
        } else {
      // Если нет studentSlug, это обычная регистрация преподавателя
      setIsInviteMode(false);
      setFormData(prev => ({
        ...prev,
        role: 'teacher'
      }));
    }
  }, [studentSlug]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  // Секретный код для регистрации преподавателей (можно изменить)
  const TEACHER_INVITE_CODE = process.env.REACT_APP_TEACHER_INVITE_CODE || 'SamKri06179695';

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Please enter your name';
    if (!formData.email) newErrors.email = 'Please enter your email';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email address';
    if (!formData.password) newErrors.password = 'Please enter a password';
    else if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    if (!formData.agreement) newErrors.agreement = 'You must accept the agreement';
    
    // Проверка invite кода для преподавателей (если не студент по invite link)
    if (formData.role === 'teacher' && !isInviteMode) {
      if (!formData.inviteCode) {
        newErrors.inviteCode = 'Invite code is required for teacher registration';
      } else if (formData.inviteCode !== TEACHER_INVITE_CODE) {
        newErrors.inviteCode = 'Invalid invite code';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    setErrors({});

    try {
      // Блокируем регистрацию студентов без invite-ссылки
      if (formData.role === 'student' && !isInviteMode) {
        setErrors({ general: 'Student registration is only available through invitation links.' });
        setIsLoading(false);
        return;
      }

      console.log('Form submission started:', { formData, isInviteMode, inviteData });

      // 1. Проверяем, существует ли пользователь с таким email
      // Проверяем в teachers (для преподавателей) или users (для студентов)
      if (formData.role === 'teacher') {
        const { data: existingTeacher } = await supabase
          .from('teachers')
          .select('email')
          .eq('email', formData.email)
          .single();

        if (existingTeacher) {
          setErrors({ email: 'A teacher with this email already exists' });
          setIsLoading(false);
          return;
        }
      } else {
        // Для студентов НЕ проверяем заранее - Supabase Auth сам проверит
        // Если email уже существует, обработаем это ниже
      }

      // 2. Создаем пользователя в Supabase Auth (с подтверждением email)
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            role: formData.role,
            name: formData.name,
            // For student invite — EmailConfirmation reads this to link the
            // existing student record to the new auth user after email is confirmed.
            ...(isInviteMode && studentSlug ? { student_invite_slug: studentSlug } : {})
          }
        }
      });

      // Обрабатываем случай, когда пользователь уже существует
      if (authError) {
        // Если пользователь уже зарегистрирован, пробуем войти
        if (authError.message?.includes('already registered') || authError.message?.includes('already exists')) {
          console.log('User already exists, attempting to sign in...');
          
          // Пробуем войти с этим email и паролем
          const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
            email: formData.email,
            password: formData.password,
          });

          if (signInError) {
            setErrors({ email: 'This email is already registered. Please use the correct password or contact support.' });
            setIsLoading(false);
            return;
          }

          // Если вход успешен, используем существующего пользователя
          const existingUserId = signInData.user?.id;
          if (!existingUserId) {
            throw new Error('Failed to get user ID from existing account');
          }

          // Продолжаем с существующим пользователем
          const userId = existingUserId;
          
          // Проверяем, есть ли запись в users
          const { data: existingUserData, error: userCheckError } = await supabase
            .from('users')
            .select('*')
            .eq('id', userId)
            .single();

          let userData;
          
          if (userCheckError || !existingUserData) {
            // Если записи нет в users, создаем её
            const passwordHash = await bcrypt.hash(formData.password, 10);
            const { data: newUserData, error: userError } = await supabase
              .from('users')
              .insert({
                id: userId,
                email: formData.email,
                password_hash: passwordHash,
                name: formData.name,
                role: 'student'
              })
              .select()
              .single();

            if (userError && userError.code !== '23505') {
              throw userError;
            }

            userData = newUserData || {
              id: userId,
              email: formData.email,
              name: formData.name,
              role: 'student'
            };
          } else {
            // Обновляем имя, если оно изменилось
            if (existingUserData.name !== formData.name) {
              const { data: updatedUserData } = await supabase
                .from('users')
                .update({ name: formData.name })
                .eq('id', userId)
                .select()
                .single();
              
              userData = updatedUserData || existingUserData;
            } else {
              userData = existingUserData;
            }
          }

          // Обновляем студента в students таблице
          if (isInviteMode && studentSlug) {
            try {
              const student = inviteData || await getStudentByInviteSlug(studentSlug);
              if (student && student.id) {
                console.log('Updating existing student with new data:', {
                  studentId: student.id,
                  email: formData.email,
                  name: formData.name,
                  user_account_id: userId
                });
                
                await updateStudent(student.id, {
                  user_account_id: userId,
                  email: formData.email,
                  name: formData.name // Обновляем имя студента данными из формы
                });
                
                console.log('Student updated successfully');
              }
            } catch (updateError) {
              console.error('Error updating student during existing user registration:', updateError);
              // Продолжаем, даже если обновление не удалось
            }
          }

          // Сохраняем в localStorage
      const newUser = {
            id: userData.id,
            name: userData.name,
            email: userData.email,
            role: userData.role
          };

          localStorage.setItem('currentUser', JSON.stringify(newUser));
          localStorage.setItem('isAuthenticated', 'true');

          setSuccess(true);
          setIsLoading(false);

          if (isInviteMode && studentSlug) {
            setTimeout(() => {
              navigate(`/student-dashboard/${studentSlug}`);
              window.location.reload();
            }, 1500);
          } else {
            setTimeout(() => {
              navigate('/');
              window.location.reload();
            }, 1500);
          }
          return;
        }
        
        // Если другая ошибка, выбрасываем её
        throw authError;
      }

      const userId = authData.user?.id;
      if (!userId) {
        throw new Error('Failed to get user ID from authentication');
      }

      // ── EMAIL CONFIRMATION CHECK ──────────────────────────────────────────
      // If Supabase has "Confirm email" enabled, the signUp response is one
      // of two shapes depending on project config / gotrue version:
      //   1) data.session = null, data.user.email_confirmed_at = null
      //   2) data.session = <pre-auth session>, data.user.email_confirmed_at = null
      // In BOTH cases the row cannot be inserted into teachers / students
      // because RLS policies usually require a confirmed user (or auth.uid()
      // is treated as anon for unconfirmed accounts). Detect either case via
      // email_confirmed_at and defer the insert until EmailConfirmation runs
      // verifyOtp (which gives us a fully-confirmed session).
      const emailConfirmedAt = authData.user?.email_confirmed_at
        || authData.user?.confirmed_at; // older field name in some SB versions
      if (!authData.session || !emailConfirmedAt) {
        try {
          if (isInviteMode && studentSlug) {
            localStorage.setItem('pendingStudentInviteSlug', studentSlug);
          }
          localStorage.setItem('pendingRegistrationEmail', formData.email);
          localStorage.setItem('pendingRegistration', JSON.stringify({
            role: formData.role,
            name: formData.name,
            email: formData.email,
            inviteSlug: studentSlug || null,
          }));
        } catch (_) {}
        setShowEmailCheck(true);
        setIsLoading(false);
        return;
      }

      let userData;

      if (formData.role === 'teacher') {
        // 4. Сохраняем преподавателя в таблицу teachers
        const { data: teacherData, error: teacherError } = await supabase
          .from('teachers')
          .insert({
            auth_user_id: userId, // Связь с auth.users
            email: formData.email,
            name: formData.name,
            phone: null
          })
          .select()
          .single();

        if (teacherError) {
          console.error('Error inserting teacher into teachers table:', teacherError);
          if (teacherError.code === '23505') { // Unique violation
            const { data: existingTeacherData } = await supabase
              .from('teachers')
              .select('*')
              .eq('auth_user_id', userId)
              .single();
            
            if (existingTeacherData) {
              userData = {
                id: existingTeacherData.auth_user_id,
                name: existingTeacherData.name,
                email: existingTeacherData.email,
                role: 'teacher'
              };
              localStorage.setItem('currentUser', JSON.stringify(userData));
              localStorage.setItem('isAuthenticated', 'true');
              setSuccess(true);
              setIsLoading(false);
              setTimeout(() => {
                navigate('/');
                window.location.reload();
              }, 2000);
              return;
            }
          }
          throw teacherError;
        }

        userData = {
          id: teacherData.auth_user_id,
          name: teacherData.name,
          email: teacherData.email,
          role: 'teacher'
        };
      } else {
        // 4. Сохраняем студента в таблицу users (для совместимости)
        const passwordHash = await bcrypt.hash(formData.password, 10);
        
        const { data: newUserData, error: userError } = await supabase
          .from('users')
          .insert({
            id: userId,
            email: formData.email,
            password_hash: passwordHash,
        name: formData.name,
            role: 'student'
          })
          .select()
          .single();

        if (userError) {
          console.error('Error inserting user into users table:', userError);
          if (userError.code === '23505') {
            const { data: existingUserData } = await supabase
              .from('users')
              .select('*')
              .eq('id', userId)
              .single();
            
            if (existingUserData) {
              userData = {
                id: existingUserData.id,
                name: existingUserData.name,
                email: existingUserData.email,
                role: existingUserData.role
              };
              localStorage.setItem('currentUser', JSON.stringify(userData));
              localStorage.setItem('isAuthenticated', 'true');
              setSuccess(true);
              setIsLoading(false);
              setTimeout(() => {
                navigate('/');
                window.location.reload();
              }, 2000);
              return;
            }
          }
          throw userError;
        }

        userData = {
          id: newUserData.id,
          name: newUserData.name,
          email: newUserData.email,
          role: newUserData.role
        };
      }

      console.log('New user created in Supabase:', userData);
      console.log('User ID (from auth):', userId);
      console.log('User ID (from userData):', userData.id);

      // 5. Автоматически логиним пользователя ПЕРЕД обновлением студента
      // Это критически важно - RLS политика требует authenticated пользователя
      const { error: loginError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      // If Supabase has "Confirm email" turned on, the user can NOT log in
      // until they click the confirmation link. Show the "check your inbox"
      // screen instead of throwing. For student invite mode, stash the
      // invite slug so EmailConfirmation.jsx can finish the linking after
      // verifyOtp succeeds (which establishes a session).
      if (loginError && /confirm/i.test(loginError.message || '')) {
        try {
          if (isInviteMode && studentSlug) {
            localStorage.setItem('pendingStudentInviteSlug', studentSlug);
          }
          localStorage.setItem('pendingRegistrationEmail', formData.email);
        } catch (_) {}
        setShowEmailCheck(true);
        setIsLoading(false);
        return;
      }

      if (loginError) {
        throw loginError;
      }
      
      // Проверяем, что сессия установлена
      const { data: { session } } = await supabase.auth.getSession();
      if (!session || !session.user) {
        throw new Error('Session not established after login');
      }
      
      console.log('✅ User logged in successfully');
      console.log('✅ Session established, user ID:', session.user.id);
      console.log('✅ auth.uid() should be:', session.user.id);
      console.log('✅ Now can update student with user_account_id');

      // 6. Сохраняем в localStorage для совместимости
      const newUser = {
        id: userId, // Используем userId из auth, чтобы гарантировать правильный ID
        name: userData.name,
        email: userData.email,
        role: userData.role,
        createdAt: userData.created_at
      };

      localStorage.setItem('currentUser', JSON.stringify(newUser));
      localStorage.setItem('isAuthenticated', 'true');

      // 6.5. Если это регистрация студента, ОБНОВЛЯЕМ user_account_id ПОСЛЕ логина
      // Это критически важно - пользователь должен быть authenticated для RLS
      if (isInviteMode && studentSlug) {
        console.log('=== UPDATING STUDENT AFTER LOGIN ===');
        console.log('Student slug:', studentSlug);
        console.log('User ID to link:', userId);
        console.log('User is now authenticated:', true);
        
        try {
          // Получаем студента по slug
          const studentToUpdate = inviteData || await getStudentByInviteSlug(studentSlug);
          
          if (studentToUpdate && studentToUpdate.id) {
            console.log('Found student to update, ID:', studentToUpdate.id);
            
            // ВАЖНО: Получаем ID из текущей сессии для гарантии правильного auth.uid()
            const { data: { session: currentSession } } = await supabase.auth.getSession();
            const currentUserId = currentSession?.user?.id || userId;
            
            console.log('Updating student with:');
            console.log('- studentId:', studentToUpdate.id);
            console.log('- user_account_id:', currentUserId);
            console.log('- email:', formData.email);
            console.log('- name:', formData.name);
            console.log('- auth.uid() should be:', currentUserId);
            
            // Обновляем ПОСЛЕ логина, когда пользователь authenticated
            // Пробуем несколько методов обновления для надежности
            let updateResult = null;
            let updateSuccess = false;
            
            // Метод 1: RPC функция (самый надежный)
            try {
              console.log('Attempting update via RPC function...');
              const { data: rpcData, error: rpcError } = await supabase.rpc('update_student_on_registration', {
                p_student_id: studentToUpdate.id,
                p_user_account_id: currentUserId,
                p_email: formData.email,
                p_name: formData.name
              });
              
              if (!rpcError && rpcData && rpcData.length > 0) {
                updateResult = {
                  ...rpcData[0],
                  slug: rpcData[0].invite_slug,
                  studentId: rpcData[0].id,
                  studentSlug: rpcData[0].invite_slug
                };
                updateSuccess = true;
                console.log('✅ RPC function success!', updateResult);
              } else {
                console.warn('RPC function failed:', rpcError);
              }
            } catch (rpcErr) {
              console.warn('RPC function error:', rpcErr);
            }
            
            // Метод 2: updateStudent (fallback)
            if (!updateSuccess) {
              try {
                console.log('Attempting update via updateStudent...');
                updateResult = await updateStudent(studentToUpdate.id, {
                  user_account_id: currentUserId,
                  email: formData.email,
                  name: formData.name
                });
                updateSuccess = true;
                console.log('✅ updateStudent success!', updateResult);
              } catch (updateErr) {
                console.error('updateStudent failed:', updateErr);
              }
            }
            
            // Метод 3: Прямой SQL через link_student_to_user (последний fallback)
            if (!updateSuccess) {
              try {
                console.log('Attempting update via link_student_to_user...');
                const { data: linkData, error: linkError } = await supabase.rpc('link_student_to_user', {
                  p_student_email: formData.email,
                  p_user_id: currentUserId
                });
                
                if (!linkError && linkData) {
                  // Получаем обновленного студента
                  const updatedStudent = await getStudentByInviteSlug(studentSlug);
                  if (updatedStudent && updatedStudent.user_account_id) {
                    updateResult = updatedStudent;
                    updateSuccess = true;
                    console.log('✅ link_student_to_user success!', updateResult);
                  }
                } else {
                  console.warn('link_student_to_user failed:', linkError);
                }
              } catch (linkErr) {
                console.error('link_student_to_user error:', linkErr);
              }
            }
            
            console.log('✅ Student updated AFTER login:', updateResult);
            console.log('✅ user_account_id set to:', currentUserId);
            console.log('✅ Updated student data:', {
              id: updateResult?.id,
              user_account_id: updateResult?.user_account_id,
              email: updateResult?.email,
              name: updateResult?.name,
              invite_slug: updateResult?.invite_slug
            });
            
            if (!updateResult || !updateResult.user_account_id) {
              console.error('❌ CRITICAL: user_account_id not set after update!');
              console.error('Update result:', updateResult);
              throw new Error('Failed to set user_account_id in students table');
            } else if (updateResult.user_account_id !== currentUserId) {
              console.error('❌ CRITICAL: user_account_id mismatch!');
              console.error('Expected:', currentUserId);
              console.error('Got:', updateResult.user_account_id);
              throw new Error('user_account_id mismatch after update');
            } else {
              console.log('✅ SUCCESS: user_account_id correctly set to:', currentUserId);
            }
          } else {
            console.error('❌ Student not found for update');
          }
        } catch (updateError) {
          console.error('❌ ERROR updating student after login:', updateError);
          console.error('Update error details:', {
            message: updateError.message,
            code: updateError.code,
            details: updateError.details,
            hint: updateError.hint
          });
          
          // НЕ выбрасываем ошибку, чтобы не блокировать регистрацию
          // Вместо этого показываем предупреждение и продолжаем
          console.warn('⚠️ WARNING: Student update failed, but registration continues');
          console.warn('Student will need to contact support to link their account');
          
          // Показываем пользователю предупреждение
          setErrors({ 
            general: 'Registration successful, but student profile linking failed. Please contact support with your email: ' + formData.email
          });
        }
      }

      // 7. Проверяем, что студент был обновлен (для студентов)
      if (isInviteMode && studentSlug) {
        console.log('=== VERIFYING STUDENT UPDATE ===');
        console.log('User ID:', userId);
        console.log('Student slug:', studentSlug);
        
        // Проверяем, что обновление прошло успешно
        try {
          const { getStudentByUserId } = await import('../utils/studentsService');
          const verifyStudent = await getStudentByUserId(userId);
          
          if (verifyStudent && verifyStudent.user_account_id === userId) {
            console.log('✅ VERIFICATION SUCCESS: Student found by user_account_id');
            console.log('Student data:', {
              id: verifyStudent.id,
              name: verifyStudent.name,
              email: verifyStudent.email,
              user_account_id: verifyStudent.user_account_id,
              invite_slug: verifyStudent.invite_slug
            });
          } else {
            console.error('❌ VERIFICATION FAILED: Student not found by user_account_id');
            console.error('This means the update did not work correctly');
          }
          
          // Проверяем, что студент был обновлен, перед редиректом
          console.log('=== FINAL CHECK BEFORE REDIRECT ===');
          console.log('User ID:', newUser.id);
          console.log('Student slug:', inviteData?.invite_slug || studentSlug);
          
          // Даем время на обновление в базе данных
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Проверяем, что студент теперь доступен по user_account_id
          try {
            const { getStudentByUserId } = await import('../utils/studentsService');
            const verifyStudent = await getStudentByUserId(newUser.id);
            console.log('Verification: Student found by user_account_id:', verifyStudent);
            
            if (!verifyStudent) {
              console.warn('⚠️ WARNING: Student not found by user_account_id after registration');
              console.warn('This might mean the update did not complete successfully');
              console.warn('Student may need to wait a moment before logging in');
            } else {
              console.log('✅ SUCCESS: Student verified and ready for login');
              console.log('Student invite_slug:', verifyStudent.invite_slug);
            }
          } catch (verifyError) {
            console.error('Error verifying student after registration:', verifyError);
          }
          
          setSuccess(true);
          setIsLoading(false);
          
          // Используем studentSlug из URL для редиректа (надежнее чем inviteData.invite_slug)
          const redirectSlug = inviteData?.invite_slug || studentSlug;
          console.log('Redirecting to student dashboard:', `/student-dashboard/${redirectSlug}`);
          
          setTimeout(() => {
            navigate(`/student-dashboard/${redirectSlug}`);
            window.location.reload();
          }, 1500);
          return;
        } catch (error) {
          console.error('Error updating student:', error);
          // Продолжаем с редиректом даже если обновление не удалось
          setSuccess(true);
          setIsLoading(false);
          const redirectSlug = inviteData?.invite_slug || studentSlug;
                 setTimeout(() => {
            navigate(`/student-dashboard/${redirectSlug}`);
            window.location.reload();
          }, 1500);
          return;
               }
             }

      setSuccess(true);
      setIsLoading(false);

      // 8. Редирект для обычной регистрации
             if (!isInviteMode) {
               setTimeout(() => {
                 console.log('Redirecting to home');
                 navigate('/');
          window.location.reload();
               }, 2000);
             }
    } catch (error) {
      console.error('Error during form submission:', error);
      setIsLoading(false);
      setErrors({
        general: error.message || 'An error occurred during registration. Please try again.',
        email: error.message?.includes('email') ? error.message : undefined
      });
    }
  };

  // Resend the confirmation email if the first one got lost / expired.
  const handleResendConfirmation = async () => {
    setResendStatus('sending');
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: formData.email,
        options: { emailRedirectTo: `${window.location.origin}/auth/callback` }
      });
      if (error) throw error;
      setResendStatus('sent');
      setTimeout(() => setResendStatus(null), 4000);
    } catch (e) {
      console.warn('Resend confirmation failed:', e?.message);
      setResendStatus('error');
      setTimeout(() => setResendStatus(null), 4000);
    }
  };

  return (
    <div className="register-split">
      <div className="register-video">
        <video
          src={process.env.PUBLIC_URL + '/Registration.mp4'}
          autoPlay
          loop
          muted
          playsInline
          className="register-bg-video"
        />
      </div>
      <div className="register-side">
        <div className="register-container">
          <div className="register-card">
            <div className="register-tabs">
              <button className="tab active">Sign Up</button>
              <Link to="/login" className="tab">Sign In</Link>
            </div>
            {showEmailCheck ? (
              <div className="register-success">
                <div style={{ fontSize: 44, lineHeight: 1, marginBottom: 12 }}>📬</div>
                <h2>Check your inbox</h2>
                <p>
                  We sent a confirmation link to <b>{formData.email}</b>.
                  Click it to finish creating your account
                  {isInviteMode ? ' and open your lessons.' : '.'}
                </p>
                <p style={{ marginTop: 8, color: '#64748b', fontSize: 13 }}>
                  No email? Check your spam folder, or click below to send it again.
                </p>
                <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={handleResendConfirmation}
                    disabled={resendStatus === 'sending'}
                    style={{
                      padding: '10px 18px',
                      borderRadius: 10,
                      border: '1px solid #cbd5e1',
                      background: '#fff',
                      cursor: resendStatus === 'sending' ? 'wait' : 'pointer',
                      fontWeight: 600,
                      fontSize: 13,
                      color: '#1e293b',
                      minWidth: 200
                    }}
                  >
                    {resendStatus === 'sending' ? 'Sending…'
                      : resendStatus === 'sent' ? '✓ Email sent — check inbox'
                      : resendStatus === 'error' ? 'Failed — try again'
                      : 'Resend confirmation email'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowEmailCheck(false); }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#6366f1',
                      cursor: 'pointer',
                      fontSize: 12,
                      textDecoration: 'underline'
                    }}
                  >
                    Wrong email? Go back and edit
                  </button>
                </div>
              </div>
            ) : success ? (
              <div className="register-success">
                <h2>Registration successful!</h2>
                <p>Welcome to NOVAS, <b>{formData.name}</b>!</p>
                <p>Redirecting to dashboard...</p>
              </div>
            ) : (
                <form className="register-form" onSubmit={handleSubmit}>
                {/* Loading state */}
                {isLoading && studentSlug && !inviteData && (
                  <div style={{ padding: '20px', textAlign: 'center' }}>
                    <p>Loading invitation...</p>
                  </div>
                )}

                {/* Invite Error - показываем поверх формы */}
                {inviteError && (
                  <div className="invite-error" style={{ marginBottom: '20px' }}>
                    <div className="error-icon">❌</div>
                    <h3>Invalid Invite Link</h3>
                    <p>{inviteError}</p>
                    <p>Please check the link or contact your teacher for a new invitation.</p>
                    <Link to="/register" className="back-to-register">Back to Registration</Link>
                  </div>
                )}

                {/* Invite Information - показываем если есть studentSlug */}
                {studentSlug && (
                  <div className="invite-info">
                    <div className="lava-blob"></div>
                    <div className="invite-header">
                      <span className="invite-icon">🎓</span>
                      <h3>Student Invitation</h3>
                    </div>
                    {inviteData ? (
                      <>
                        <p>You've been invited to join <strong>{inviteData.teacherName || 'NOVAS'}</strong> as a student.</p>
                        <div className="invite-details">
                          <span className="invite-label">Student Name:</span>
                          <span className="invite-value">{inviteData.name || 'Not specified'}</span>
                        </div>
                      </>
                    ) : (
                      <p>You've been invited to join <strong>NOVAS</strong> as a student.</p>
                    )}
                  </div>
                )}

                {/* Показываем выбор роли только в invite-режиме (для студентов) */}
                {isInviteMode ? (
                <div className="form-group">
                  <label htmlFor="role">Role</label>
                  <select 
                    name="role" 
                    id="role" 
                    value={formData.role} 
                    onChange={handleInputChange}
                      disabled={true}
                  >
                    <option value="student"> Student</option>
                  </select>
                    <small className="invite-note">Role is set to Student for this invitation</small>
                  </div>
                ) : (
                  // Для обычной регистрации - только преподаватели, роль скрыта
                  <input type="hidden" name="role" value="teacher" />
                )}
                {/* Invite Code для преподавателей */}
                {formData.role === 'teacher' && !isInviteMode && (
                  <div className="form-group">
                    <label htmlFor="inviteCode">Invite Code <span style={{color: 'red'}}>*</span></label>
                    <input
                      type="text"
                      id="inviteCode"
                      name="inviteCode"
                      value={formData.inviteCode}
                      onChange={handleInputChange}
                      placeholder="Enter invite code"
                      className={errors.inviteCode ? 'error' : ''}
                    />
                    {errors.inviteCode && <span className="error-message">{errors.inviteCode}</span>}
                    <small style={{color: '#666', fontSize: '12px'}}>Registration is by invitation only. Please contact support for an invite code.</small>
                </div>
                )}
                <div className="form-group">
                  <label htmlFor="name">Your name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your name"
                    autoComplete="name"
                    className={errors.name ? 'error' : ''}
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="example@email.com"
                    autoComplete="email"
                    readOnly={isInviteMode && !!inviteData?.email}
                    className={(errors.email ? 'error' : '') + ((isInviteMode && inviteData?.email) ? ' readonly' : '')}
                    title={isInviteMode && inviteData?.email ? 'Your teacher set this email — log in with it after confirming' : undefined}
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                  {isInviteMode && inviteData?.email && (
                    <span className="field-hint" style={{ display: 'block', marginTop: 4, fontSize: 11, color: '#64748b' }}>
                      Set by your teacher — confirm this address to access your lessons.
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter password"
                      autoComplete="new-password"
                      className={errors.password ? 'error' : ''}
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowPassword(v => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M17.94 17.94A10.06 10.06 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.password && <span className="error-message">{errors.password}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Confirm password"
                      autoComplete="new-password"
                      className={errors.confirmPassword ? 'error' : ''}
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowConfirmPassword(v => !v)}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M17.94 17.94A10.06 10.06 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
                </div>
                <div className="form-group agreement-group">
                  <input
                    type="checkbox"
                    id="agreement"
                    name="agreement"
                    checked={formData.agreement}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="agreement" className="agreement-label">
                    I accept the <a href="/user-agreement" target="_blank" rel="noopener noreferrer">User Agreement</a> and <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                  </label>
                </div>
                {errors.agreement && <span className="error-message">{errors.agreement}</span>}
                {errors.general && <span className="error-message">{errors.general}</span>}
                <button
                  type="submit"
                  className={`register-button ${isLoading ? 'loading' : ''}`}
                  disabled={isLoading || !formData.agreement}
                >
                  {isLoading ? (
                    <div className="login-loading-container">
                      <div className="login-spinner"></div>
                      <span className="login-loading-text">Signing in...</span>
                    </div>
                  ) : (
                    'Sign Up'
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register; 